import { Box, Text, Title } from "@dehub/theme";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import GoogleMapPin from "@/components/google-map";
import type { IGeneral } from "@/models/General";
import { renderName } from "@/utils/render-name";
import type { IBranchRow, IWarehouse } from "./forms/warehouse-form";
import classes from "../warehouse.module.css";

type Field = { key: string; label: string; value: ReactNode; wide?: boolean };

type Props = Readonly<{
  warehouse: IWarehouse & {
    warehouseUser?: { firstName?: string; lastName?: string } | null;
    buyers?: { id: string; refCode?: string }[];
    suppliers?: { id: string; refCode?: string }[];
  };
  branches?: { rows?: IBranchRow[] };
}>;

const DEFAULT_LAT = 47.9192;
const DEFAULT_LNG = 106.9172;

function Section({ title, fields }: Readonly<{ title: string; fields: Field[] }>) {
  return (
    <Box component="section" className={classes.card}>
      <Title order={2} className={classes.cardTitle}>
        {title}
      </Title>
      <Box component="dl" className={classes.fields}>
        {fields.map((field) =>
          field.label === "" ? (
            <Box key={field.key} aria-hidden="true" />
          ) : (
          <Box key={field.key} className={`${classes.field} ${field.wide ? classes.fieldWide : ""}`}>
            <Box component="dt" className={classes.fieldLabel}>
              {field.label}
            </Box>
            <Box component="dd" className={classes.fieldValue}>
              {field.value === null || field.value === undefined || field.value === "" ? "-" : field.value}
            </Box>
          </Box>
          ),
        )}
      </Box>
    </Box>
  );
}

/** Read-only view of a warehouse (canvas Prt_WhDetail): label / value sections plus the location card. */
export function WarehouseView({ warehouse, branches }: Props) {
  const { t } = useTranslation("warehouse");
  const { t: tCommon } = useTranslation("common");
  const zipCodes = useSelector((state: { general: IGeneral }) => state.general.zipCodes) as { code: string; name: string }[] | undefined;
  const zipName = (code?: string) => zipCodes?.find((z) => z.code === code)?.name ?? code;
  const yesNo = (value?: boolean) => (value ? t("form.yes") : t("form.no"));
  const branch = branches?.rows?.find((b) => b.id === warehouse.branchId);
  const codes = (list?: { refCode?: string }[]) => (list ?? []).map((item) => item.refCode).filter(Boolean).join(", ");

  const lat = parseFloat(warehouse.locationLat ?? "");
  const lng = parseFloat(warehouse.locationLng ?? "");
  const hasLocation = Number.isFinite(lat) && Number.isFinite(lng);
  const center = { lat: hasLocation ? lat : DEFAULT_LAT, lng: hasLocation ? lng : DEFAULT_LNG };

  return (
    <Box className={classes.viewGrid}>
      <Box className={classes.viewMain}>
        <Section
          title={t("detail.general")}
          fields={[
            { key: "code", label: t("form.warehouseCode"), value: warehouse.refCode },
            { key: "branch", label: t("form.branch"), value: branch?.name },
            { key: "phone", label: t("form.phone"), value: warehouse.phone },
            { key: "keeper", label: t("form.responsible"), value: warehouse.warehouseUser ? renderName(warehouse.warehouseUser) : null },
            { key: "regUser", label: t("form.registeredBy"), value: warehouse.regUser ? renderName(warehouse.regUser) : null },
            { key: "main", label: t("form.isDefault"), value: yesNo(warehouse.isDefault) },
          ]}
        />
        <Section
          title={t("detail.address")}
          fields={[
            { key: "province", label: tCommon("address.cityProvince"), value: zipName(warehouse.province) },
            { key: "district", label: tCommon("address.district"), value: zipName(warehouse.district) },
            { key: "khoroo", label: tCommon("address.khoroo"), value: zipName(warehouse.khoroo) },
            { key: "street", label: tCommon("address.street"), value: warehouse.khoroolol },
            { key: "building", label: tCommon("address.building"), value: warehouse.khotkhonBair },
            { key: "fence", label: tCommon("address.fence"), value: warehouse.khashaaDavkhar },
            { key: "door", label: tCommon("address.door"), value: warehouse.khaalgaDugaar },
            { key: "detail", label: t("form.addressDetail"), value: warehouse.warehouseAddress, wide: true },
          ]}
        />
        <Section
          title={t("detail.goods")}
          fields={[
            { key: "receives", label: t("detail.receives"), value: yesNo(warehouse.isBuyer) },
            { key: "buyers", label: t("form.buyerBiz"), value: warehouse.isBuyer ? codes(warehouse.buyers) : null },
            { key: "gap1", label: "", value: "" },
            { key: "ships", label: t("detail.ships"), value: yesNo(warehouse.isSupplier) },
            { key: "suppliers", label: t("form.supplierBiz"), value: warehouse.isSupplier ? codes(warehouse.suppliers) : null },
          ]}
        />
      </Box>
      <Box component="aside" className={`${classes.card} ${classes.viewAside}`}>
        <Box h={220}>
          <GoogleMapPin center={center} zoom={16} readOnly hideMarker={!hasLocation} />
        </Box>
        <Box className={classes.asideBody}>
          <Text fz={15} fw={600}>{t("detail.location")}</Text>
          <Text ff="monospace" fz={14} c="gray.8">
            {hasLocation ? `${lat.toFixed(4)}, ${lng.toFixed(4)}` : t("detail.locationNotSet")}
          </Text>
          <Text fz={14} c="gray.8">{warehouse.warehouseAddress || "-"}</Text>
        </Box>
      </Box>
    </Box>
  );
}
